import { useEffect, useRef, useState } from "react";

import type { Locale } from "../../../shared/types";
import { acquireBlob, type BlobRenderer } from "../blob/renderer";
import { useCopy } from "../lib/copy";
import Landing from "./Landing";

type Phase = "booting" | "ready" | "submitting";

/**
 * Signed out is two views in one place: the landing first — what this is, what you
 * leave with, one way in — and the auth form after the one primary action. A view
 * swap, not a route: the blob, the bar and the status line never remount.
 */
type View = "landing" | "form";

/**
 * What the server said and what it called it. The code is carried alongside the
 * message because the message alone cannot say WHICH field is wrong, and marking
 * both fields on every failure is how a form tells somebody their correct email
 * is also a mistake.
 */
export interface AuthFailure {
  message: string;
  code: string | null;
}

interface AuthProps {
  locale: Locale;
  phase: Phase;
  error: AuthFailure | null;
  /** Nobody has signed in from this browser, so "Welcome back" would be a lie. */
  firstVisit: boolean;
  /**
   * From GET /api/config. "private" means the door is shut: no create-account
   * anywhere, and the landing says access is limited in one quiet sentence.
   */
  access: "private" | "open";
  onSubmit: (mode: "signIn" | "signUp", email: string, password: string) => void;
  /** The language switch has to exist before the account does, or it writes `en`. */
  onChangeLocale: (locale: Locale) => void;
}

/** Endonyms, as in the signed-in bar: the other language names itself. */
const OTHER_LANGUAGE: Record<Locale, string> = { en: "Русский", ru: "English" };

/**
 * The one rule, and it is the server's rule: `registerSchema` refuses under eight
 * characters. Stated on the form so nobody meets it by being rejected, and
 * checked on the form so nobody meets it by waiting five seconds for a round trip
 * to say what could have been said while they typed.
 */
const MIN_PASSWORD = 8;

/**
 * Deliberately loose. This is not the arbiter — the server's zod `.email()` is,
 * and a client-side pattern strict enough to be interesting is a pattern strict
 * enough to reject somebody's real address. It catches the two things people
 * actually do (no @, nothing after the dot) and lets everything else through to
 * be decided properly.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Which field a failure belongs to, so exactly one of them is marked. */
type FieldName = "email" | "password";

export default function Auth({
  locale,
  phase,
  error,
  firstVisit,
  access,
  onSubmit,
  onChangeLocale,
}: AuthProps) {
  const copy = useCopy(locale);
  const [view, setView] = useState<View>("landing");
  const [mode, setMode] = useState<"signIn" | "signUp">(firstVisit ? "signUp" : "signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  /** The eye. Off by default; a password on screen is a password over a shoulder. */
  const [revealed, setRevealed] = useState(false);
  /**
   * The form's own verdict, and which field it is about. Cleared the moment that
   * field is edited: a complaint that outlives the thing it complained about is
   * the reason forms feel broken.
   */
  const [local, setLocal] = useState<{ field: FieldName; message: string } | null>(null);
  /**
   * Which fields have been left once. Validating on blur is what makes the form
   * tell you before you submit; validating on every keystroke tells you your
   * address is invalid after the first letter of it.
   */
  const [touched, setTouched] = useState<{ email: boolean; password: boolean }>({
    email: false,
    password: false,
  });

  const blobRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<BlobRenderer | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  // The same blob as the conversation's, borrowed rather than built: signing in
  // used to destroy this context and compile the stage's shader from scratch at
  // exactly the moment the founder is waiting on the page.
  useEffect(() => {
    const host = blobRef.current;
    if (!host) return;
    const { canvas, renderer } = acquireBlob();
    host.appendChild(canvas);
    rendererRef.current = renderer;
    renderer.start();
    return () => {
      rendererRef.current = null;
      renderer.stop();
      renderer.setLevel(0);
      canvas.remove();
    };
  }, []);

  // The blob is the loading indicator for sign-in exactly as it is for a reply. There
  // is nothing else on this screen that spins, fills or shimmers.
  useEffect(() => {
    rendererRef.current?.setState(phase === "ready" ? "idle" : "thinking");
  }, [phase]);

  // The first field, as soon as there is one. Arriving with focus on the body meant a
  // keyboard user's first keystroke went nowhere.
  useEffect(() => {
    if (phase === "ready" && view === "form") emailRef.current?.focus();
  }, [phase, view]);

  const busy = phase !== "ready";
  // No create-account anywhere while the door is shut, whatever this browser
  // remembers about who used it last.
  const canSignUp = access === "open";
  const signingIn = mode === "signIn" || !canSignUp;

  // A refused submit puts focus on the field that was refused. Credentials are the
  // password's problem in nine cases out of ten — the address is usually right and
  // sending somebody back to retype it is an accusation.
  useEffect(() => {
    if (!error) return;
    if (error.code === "invalid_credentials") passwordRef.current?.focus();
    else emailRef.current?.focus();
  }, [error]);

  /** The form's rules, in the order somebody meets them. Null means it passes. */
  function checkEmail(value: string): string | null {
    const trimmed = value.trim();
    if (trimmed.length === 0) return copy.errEmailRequired;
    if (!EMAIL_SHAPE.test(trimmed)) return copy.errEmailInvalid;
    return null;
  }

  function checkPassword(value: string): string | null {
    if (value.length === 0) return copy.errPasswordRequired;
    // Only on the way in. An account made before this rule existed still has to be
    // able to sign in, and the length of an existing password is not this form's
    // business.
    if (!signingIn && value.length < MIN_PASSWORD) return copy.errPasswordShort;
    return null;
  }

  function validateOnBlur(field: FieldName): void {
    setTouched((current) => ({ ...current, [field]: true }));
    const message = field === "email" ? checkEmail(email) : checkPassword(password);
    if (message) setLocal({ field, message });
    else setLocal((current) => (current?.field === field ? null : current));
  }

  function submit(): void {
    if (busy) return;
    const emailProblem = checkEmail(email);
    if (emailProblem) {
      setTouched((current) => ({ ...current, email: true }));
      setLocal({ field: "email", message: emailProblem });
      emailRef.current?.focus();
      return;
    }
    const passwordProblem = checkPassword(password);
    if (passwordProblem) {
      setTouched((current) => ({ ...current, password: true }));
      setLocal({ field: "password", message: passwordProblem });
      passwordRef.current?.focus();
      return;
    }
    setLocal(null);
    onSubmit(signingIn ? "signIn" : "signUp", email.trim(), password);
  }

  /** Landing to form, with the mode the button implied. */
  function open(next: "signIn" | "signUp"): void {
    setMode(next);
    setLocal(null);
    setView("form");
  }

  /**
   * Between the two forms. The address survives the trip — somebody who typed it
   * and then discovered they are on the wrong form should not have to type it
   * again — and the password does not, because it belongs to the form it was
   * meant for.
   */
  function swap(): void {
    setMode(signingIn ? "signUp" : "signIn");
    setPassword("");
    setRevealed(false);
    setLocal(null);
    setTouched({ email: touched.email, password: false });
    passwordRef.current?.focus();
  }

  /**
   * Everything transient this screen has to say, in one live region that is never
   * recreated. Precedence is closest-first: what the form itself refuses, then what
   * the server refused, then the word for the five seconds a sign-in takes.
   */
  const shown = local?.message ?? error?.message ?? (busy ? copy.thinking : "");
  const isProblem = local !== null || error !== null;

  /**
   * Which field wears the mark. Exactly one, or — for a credential refusal — both,
   * because "that email and password do not match" is genuinely about the pair.
   */
  const emailWrong =
    (local?.field === "email" && touched.email) || error?.code === "invalid_credentials";
  const passwordWrong =
    (local?.field === "password" && touched.password) || error?.code === "invalid_credentials";

  return (
    // One column: the signed-out screen has no notes rail to make room for.
    <div className={view === "landing" ? "field field--auth field--landing" : "field field--auth"}>
      <header className="topbar">
        {/* The 44x44 slot the blob docks into on the conversation screen. On the
            form it is the way back out, which costs no layout: an escape hatch
            that only exists on the keyboard is an escape hatch most people do
            not have. */}
        {view === "form" ? (
          <button
            className="btn btn--text topbar__back"
            type="button"
            onClick={() => {
              setView("landing");
              setLocal(null);
            }}
          >
            {copy.authBack}
          </button>
        ) : (
          <div className="topbar__slot" aria-hidden="true" />
        )}
        <div className="topbar__spacer" />
        <button
          className="btn btn--text"
          type="button"
          onClick={() => onChangeLocale(locale === "en" ? "ru" : "en")}
        >
          {OTHER_LANGUAGE[locale]}
        </button>
      </header>

      <main className="stage">
        <div className="blob" aria-hidden="true" ref={blobRef} />

        {phase !== "booting" && view === "landing" && (
          <Landing
            locale={locale}
            access={access}
            onBegin={() => open(canSignUp ? "signUp" : "signIn")}
            onSignIn={() => open("signIn")}
          />
        )}

        {phase !== "booting" && view === "form" && (
          <>
            <h1 className="q--live">{signingIn ? copy.signInTitle : copy.signUpTitle}</h1>
            <p className="auth__sub">{signingIn ? copy.signInSub : copy.signUpSub}</p>

            <form
              className="auth__stack"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
              onKeyDown={(event) => {
                // Out of the form the way every other dismissable thing in this
                // product leaves: Escape.
                if (event.key === "Escape") {
                  event.preventDefault();
                  setView("landing");
                  setLocal(null);
                }
              }}
            >
              <label htmlFor="auth-email" className="sr-only">
                {copy.emailLabel}
              </label>
              <input
                id="auth-email"
                ref={emailRef}
                className="input"
                type="email"
                name="email"
                inputMode="email"
                // `username` rather than `email`: it is the token password managers
                // pair with `current-password`, and a manager that cannot see the
                // pair offers to fill neither.
                autoComplete="username"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                // Never `disabled`. A field that greys out for the five seconds a
                // sign-in takes reads as a page that has died, and it throws away
                // the caret and the selection on the way back.
                readOnly={busy}
                placeholder={copy.emailLabel}
                aria-invalid={emailWrong || undefined}
                aria-describedby="auth-status"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setLocal((current) => (current?.field === "email" ? null : current));
                }}
                onBlur={() => validateOnBlur("email")}
              />

              <label htmlFor="auth-password" className="sr-only">
                {copy.passwordLabel}
              </label>
              <input
                  id="auth-password"
                  ref={passwordRef}
                  className="input"
                  type={revealed ? "text" : "password"}
                  name="password"
                  autoComplete={signingIn ? "current-password" : "new-password"}
                  readOnly={busy}
                  placeholder={copy.passwordLabel}
                  aria-invalid={passwordWrong || undefined}
                  aria-describedby={signingIn ? "auth-status" : "auth-rule auth-status"}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setLocal((current) => (current?.field === "password" ? null : current));
                  }}
                  onBlur={() => validateOnBlur("password")}
                />

              {/* One row under the password: the rule on the left, the eye on the
                  right. The eye is here rather than inside the field because the
                  label is a WORD — "SHOW PASSWORD", and half again as long in
                  Russian — and a word that long inside a 360px field eats the
                  field. Both halves hold their slot in both modes, so moving
                  between sign-in and sign-up moves nothing below them. */}
              <div className="auth__under">
                <p id="auth-rule" className="auth__rule label">
                  {signingIn ? " " : copy.passwordRule}
                </p>
                {/* type="button", so Return in the password field still submits the
                    form rather than toggling the eye. */}
                <button
                  className="btn btn--text auth__eye label"
                  type="button"
                  aria-pressed={revealed}
                  aria-controls="auth-password"
                  onClick={() => {
                    setRevealed((current) => !current);
                    passwordRef.current?.focus();
                  }}
                >
                  {revealed ? copy.passwordHide : copy.passwordShow}
                </button>
              </div>

              <button
                className="btn btn--primary"
                type="submit"
                aria-busy={busy || undefined}
                aria-disabled={busy || undefined}
              >
                {signingIn ? copy.signInCta : copy.signUpCta}
              </button>
            </form>
          </>
        )}

        {/* Kept in the DOM at all times, in both phases and both views: recreating a
            live region stops it being watched, and a booting screen with nothing on
            it but a blob is not a loading state for somebody who has never seen the
            blob before. */}
        <p
          id="auth-status"
          className={isProblem ? "auth__status auth__status--error" : "auth__status"}
          role="status"
          aria-live="polite"
        >
          {shown}
        </p>

        {phase !== "booting" && view === "form" && canSignUp && (
          <button className="btn btn--text auth__alt" type="button" onClick={swap}>
            {signingIn ? copy.signUpSwitch : copy.signInSwitch}
          </button>
        )}
      </main>
    </div>
  );
}
